import { SelectionType } from "src/js/stores/map/store";
import { useConstraintsLayerTypeContext } from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext";
import { LayerTypeSelectionSidebarProps } from "react-migration/layouts/map/Multilayer/types";
import { CollapsibleConsiderationsCard } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/AssessmentTab/CollapsibleConsiderationsCard";
import { useSelectionSidebarLoading } from "react-migration/layouts/map/Multilayer/Bundle/useSelectionSidebarLoadingState";
import { apiDesignationIsVisible } from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext/utils";
import {
  ConstraintLayerConfig,
  isSingleConstraintLayer,
} from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType";
import { useLayerZoomLimit } from "react-migration/layouts/map/Multilayer/Bundle/useLayerZoomLimit";
import { ConstraintsStore } from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext/store";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useDesignationsByGeometry } from "../../hooks/useDesignationsByGeometry";
import { OutsideZoomLimitMessage } from "./OutsideZoomLimitMessage";
import { DesignationsList } from "./DesignationsList";

export type DesignationsSelectionSidebarProps = Pick<
  LayerTypeSelectionSidebarProps,
  "selection" | "setDetailSelection" | "layer" | "onLoadingChange" | "cardExpanded"
> & { constraintsStore: ConstraintsStore; selectionType?: SelectionType };

export function DesignationsSelectionSidebar({
  selection,
  cardExpanded,
  layer,
  setDetailSelection,
  onLoadingChange,
  constraintsStore,
  selectionType,
}: DesignationsSelectionSidebarProps) {
  const { t } = useTranslation();
  const { consideration: considerationZoomLimit } = useLayerZoomLimit(layer);
  const layerConfig = layer.layerConfig as ConstraintLayerConfig;
  const rootCategories = isSingleConstraintLayer(layerConfig) ? [layerConfig.rootCategory] : layerConfig.rootCategories;
  const intersectionBuffer = layerConfig.intersectionThresholdMeters;

  const { designations, loading } = useDesignationsByGeometry({
    geometry: selection.feature?.geometry || null,
    bufferMeters: intersectionBuffer,
    rootCategories,
    attributeKeys: layerConfig.attributesToLoad,
  });

  useSelectionSidebarLoading(onLoadingChange, loading);

  const layerStore = constraintsStore.layers[layer.id];

  const visibleDesignations = designations.filter((designation) => apiDesignationIsVisible(designation, layerStore));

  if (!visibleDesignations.length) return null;

  const isOutsideZoomLimit = !!considerationZoomLimit;
  const labelTranslation = layer.label ? t(layer.label.translationKey) : undefined;
  return (
    <CollapsibleConsiderationsCard
      heading={layer.title}
      name={layer.title}
      label={labelTranslation}
      count={!isOutsideZoomLimit ? visibleDesignations.length : undefined}
      defaultExpanded={cardExpanded}
    >
      {isOutsideZoomLimit ? (
        <div className="px-2 py-3">
          <OutsideZoomLimitMessage layer={layer} />
        </div>
      ) : (
        <DesignationsList
          designations={visibleDesignations}
          setDetailSelection={setDetailSelection}
          cardName={layer.title}
          selectionType={selectionType}
        />
      )}
    </CollapsibleConsiderationsCard>
  );
}

/**
 * Connects the `DesignationsSelectionSidebar` component with the constraints
 * provider. Note that this behavior may not always be desired.
 */
export function DesignationsSelectionSidebarConnected(
  props: Omit<DesignationsSelectionSidebarProps, "constraintsStore">
) {
  const { constraintsStore } = useConstraintsLayerTypeContext();

  return <DesignationsSelectionSidebar {...props} constraintsStore={constraintsStore} />;
}
